// RBAC — the frontend mirror of the dynamic permission matrix.
// Permissions come live from role_permissions (loaded in auth.tsx); this gates
// UI (nav/route/field). Postgres RLS is the real security boundary.

export type PermLevel = 'none' | 'view' | 'edit' | 'full';
export type PermMap = Record<string, PermLevel>;

// The gated modules (must match role_permissions.module in the DB).
export const MODULES = [
  'dashboard',
  'projects',
  'pipeline',
  'talent',
  'capacity',
  'timesheets',
  'schedule',
  'forecasting',
  'inbox',
  'people',
  'contracts',
  'org',
  'reports',
  'calendar',
] as const;
export type ModuleKey = (typeof MODULES)[number];

// Workspace is one screen over two modules: its Notifications tab is `inbox`, its
// Calendar tab is `calendar`. The nav item, route and home page open it if either
// tab would show.
export const WORKSPACE_MODULES = ['inbox', 'calendar'] as const;

// ── Product scope (temporary HR-focused build) ───────────────────────────────
// Only these modules appear in the sidebar + routing right now; everything else
// is hidden until re-enabled. The DB / RLS are untouched — this is a UI scope.
// `me` (My Space) and `system` are always-available utility screens and are not
// gated by this list. To bring a module back, just add its key here.
export const ENABLED_MODULES: ReadonlySet<string> = new Set([
  'people',
  'timesheets',
  'inbox',
  'org',
  'calendar',
  'schedule',
  'capacity',
  'talent',
  'projects',
  'reports',
  'dashboard',
]);
export const isModuleEnabled = (module: string): boolean => ENABLED_MODULES.has(module);

const RANK: Record<PermLevel, number> = { none: 0, view: 1, edit: 2, full: 3 };

// Does this permission map grant at least `min` on `module`?
export const can = (perms: PermMap | undefined, module: string, min: PermLevel = 'view'): boolean =>
  !!perms && RANK[perms[module] ?? 'none'] >= RANK[min];

// Can this person open a screen gated on `module` — enabled in this build AND
// permitted? A screen spanning several modules opens if any one of them does.
export type ModuleGate = string | readonly string[];
export const canOpen = (perms: PermMap | undefined, module: ModuleGate): boolean =>
  [module].flat().some((m) => isModuleEnabled(m) && can(perms, m));

// Compensation / bank details are visible only with people=full (HR/owner/admin).
export const canSeeComp = (perms?: PermMap): boolean => can(perms, 'people', 'full');

// ── Role display metadata (default roles; custom roles fall back to id/neutral) ─
// Role colours are categorical: they tell roles apart, they never judge them, so
// no role wears a semantic hue. Owner was rose, the colour this app keeps for
// "wrong"; Member was sage ("success"), Contractor amber ("warning") and Admin
// the accent, which means selection and brand (ME-15, ORG-20; UI-CONVENTIONS §3).
// That leaves four hues (sky and cyan are one, so only sky is used), which go
// to the four roles with the most reach; owner and admin, the pair people
// confuse, are lilac and iris, far apart. Eight roles and four hues means the
// four lowest share neutral, a deliberate collision: their labels tell them
// apart, and any other hue would claim a status or pass them off as a lead.
// Every role badge, pill and bar reads its tone from roleTone(), so this map is
// the one place to change. The seeded roles.tone column still holds the old
// values; Org settings prefers this map for any role listed here.
type RoleTone = 'iris' | 'lilac' | 'sky' | 'coral' | 'neutral';
export const ROLE_META: Record<string, { label: string; tone: RoleTone }> = {
  owner: { label: 'Owner', tone: 'lilac' },
  admin: { label: 'Admin', tone: 'iris' },
  hr: { label: 'HR Manager', tone: 'coral' },
  lead: { label: 'Project Lead', tone: 'sky' },
  member: { label: 'Member', tone: 'neutral' },
  contractor: { label: 'Contractor', tone: 'neutral' },
  viewer: { label: 'Viewer', tone: 'neutral' },
  client: { label: 'Client', tone: 'neutral' },
};
export const roleLabel = (id?: string | null): string => (id ? (ROLE_META[id]?.label ?? id) : '—');
export const roleTone = (id?: string | null): RoleTone => (id && ROLE_META[id]?.tone) || 'neutral';
